import dayjs from 'dayjs';
import { isDecoratorRuleAddress } from './decoratorDestinationRules';

// SearchableAddressDropdown may emit an option ID as a numeric string.
// Do not discard it just because it is not a JavaScript Number.
const positiveAddressId = (value) => {
  if (!/^[1-9][0-9]*$/.test(String(value ?? ''))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) ? id : null;
};

const rawAddress = (address) => ({
  street: address.street,
  city: address.city,
  state: address.state,
  postal: address.postal,
  country: address.country,
});

// This is the one canonical transformation used by both single and bulk saves.
export const buildShippingPayload = ({
  formData, decoratorFormData, otherSupplierFormData, selectedSupplierName, instanceUniqueId,
  selectedSupplierId, selectedSupplierObj, selectedSupplierOption, selectedAddr,
  selectedAddrIsRaw, decClientOpt, decClientIsRaw, decoratorOptions, otherSupplierOptions = [],
  decoratorManualAccount, resolveAccountName, resolveAccountValue,
}) => {
  const supplierSource = formData.supplierSource || selectedSupplierObj?.supplierSource ||
    selectedSupplierObj?.SupplierSource || selectedSupplierOption?.supplierSource ||
    selectedSupplierOption?.SupplierSource || null;
  const isDecorator = formData.shipTo === 'decorator';
  const applyDecoratorRule = isDecorator && isDecoratorRuleAddress(
    decoratorFormData.decoratorAddress, decoratorFormData.decorator
  );

  return {
    ...(instanceUniqueId ? { instanceUniqueId } : {}),
    SupplierName: selectedSupplierName,
    SupplierId: selectedSupplierId,
    SupplierSource: supplierSource,
    Supplier: selectedSupplierObj ? { ...selectedSupplierObj, supplierSource } : null,
    isSampleOrder: formData.sampleOrder,
    InhandsDate: formData.inHandsDate ? dayjs(formData.inHandsDate).format('YYYY-MM-DD') : null,
    isFirm: formData.firm,
    SupplierNotes: formData.supplierNotes || '',
    ShippingMethod: formData.shippingMethod,
    ShipTo: {
      Type: formData.shipTo,
      AddressId: applyDecoratorRule || selectedAddrIsRaw ? null : positiveAddressId(selectedAddr.id),
      ...(applyDecoratorRule ? { ApplyDecoratorRule: true } : {}),
      CompanyAddressId: formData.shipTo === 'elite' && /^company-[1-9][0-9]*$/.test(String(formData.address || ''))
        ? Number(String(formData.address).slice(8)) : null,
      // A decorator's onward company address is independent of the supplier
      // shipping-to-decorator address; both selections are sent explicitly.
      DecoratorOnwardCompanyAddressId: isDecorator && decoratorFormData.decoratorShipTo === 'elite' &&
        /^company-[1-9][0-9]*$/.test(String(decoratorFormData.companyAddress || ''))
        ? Number(String(decoratorFormData.companyAddress).slice(8)) : null,
      // Re-saving an existing, already-resolved Elite address must not replace
      // it with a default company address.
      DecoratorOnwardAddressId: isDecorator && decoratorFormData.decoratorShipTo === 'elite' &&
        /^[1-9][0-9]*$/.test(String(decoratorFormData.companyAddress || ''))
        ? Number(decoratorFormData.companyAddress) : null,
      RawAddress: !applyDecoratorRule && selectedAddrIsRaw ? rawAddress(selectedAddr) : undefined,
      DecoratorName: isDecorator
        ? decoratorOptions.find((item) => String(item.id) === String(decoratorFormData.decorator))?.name || null
        : null,
      DecoratorId: isDecorator ? decoratorFormData.decorator : null,
      ShipTo: isDecorator ? decoratorFormData.decoratorShipTo : null,
      InhandsDate: isDecorator && decoratorFormData.inHandsDate
        ? dayjs(decoratorFormData.inHandsDate).format('YYYY-MM-DD') : null,
      IsFirm: isDecorator ? Boolean(decoratorFormData.decoratorFirm) : null,
      ShippingAccount: isDecorator ? decoratorFormData.shippingAccount || null : null,
      ShippingAccountId: isDecorator && ['client', 'our', 'supplier'].includes(decoratorFormData.shippingAccount)
        ? decoratorFormData.shippingUser || null : null,
      DecoratorShippingAccountValue: isDecorator && decoratorFormData.shippingAccount === 'other'
        ? decoratorManualAccount : null,
      ShippingMethod: isDecorator ? decoratorFormData.shippingMethod : null,
      DecoratorNotes: isDecorator ? decoratorFormData.notes : null,
      SupplierName: formData.shipTo === 'other_supplier'
        ? otherSupplierOptions.find((option) => String(option.id) === String(otherSupplierFormData.supplier))?.name || String(otherSupplierFormData.supplier || '') || null
        : null,
      SupplierAddress: formData.shipTo === 'other_supplier' ? selectedAddr?.name || null : null,
      ClientAddressId: isDecorator && decoratorFormData.decoratorShipTo === 'client' &&
        !decClientIsRaw && Number.isInteger(decClientOpt.id) ? decClientOpt.id : null,
      ClientRawAddress: isDecorator && decoratorFormData.decoratorShipTo === 'client' &&
        decClientIsRaw ? rawAddress(decClientOpt) : undefined,
    },
    ShippingAccount: {
      Type: formData.shippingAccount,
      AccountType: resolveAccountName(),
      AccountValue: resolveAccountValue(),
    },
  };
};
